import { PolicyCategory, PolicyEnvironmentType } from '../types';

import { PolicyTypeMetadata } from './policy-types-metadata';
import { getSupportedEnvironmentTypes } from './policyTypeUtils';

/**
 * Filter policy types by environment type
 * Returns true if the policy type supports the specified environment type
 */
export function filterByEnvironmentType(
  metadata: PolicyTypeMetadata,
  environmentFilter: PolicyEnvironmentType
): boolean {
  const supportedEnvTypes = getSupportedEnvironmentTypes(metadata.id);
  return supportedEnvTypes.includes(environmentFilter);
}

/**
 * Filter policy types by category
 * Returns true if the policy type matches the specified category
 */
export function filterByCategory(
  metadata: PolicyTypeMetadata,
  categoryFilter: PolicyCategory
): boolean {
  return metadata.category === categoryFilter;
}

/**
 * Filter policy types by search query
 * Searches in display name and description (case insensitive)
 */
export function filterBySearchQuery(
  metadata: PolicyTypeMetadata,
  searchQuery: string
): boolean {
  const lowerQuery = searchQuery.toLowerCase();
  const matchesName = metadata.displayName.toLowerCase().includes(lowerQuery);
  const matchesDescription = metadata.description
    .toLowerCase()
    .includes(lowerQuery);

  return matchesName || matchesDescription;
}

/**
 * Apply all filters to a list of policy types
 *
 * @param policyTypes - The policy types to filter
 * @param environmentFilter - Filter by environment type (or 'all'/'compatible' to skip)
 * @param categoryFilter - Filter by category (or 'all' to skip)
 * @param searchQuery - Search term to filter by (empty string to skip)
 * @returns Filtered list of policy types
 */
export function filterPolicyTypes(
  policyTypes: PolicyTypeMetadata[],
  environmentFilter: PolicyEnvironmentType | 'all' | 'compatible',
  categoryFilter: PolicyCategory | 'all',
  searchQuery: string
): PolicyTypeMetadata[] {
  return policyTypes.filter((metadata) => {
    // Filter by environment type (skip for 'all' and 'compatible')
    if (environmentFilter !== 'all' && environmentFilter !== 'compatible') {
      if (!filterByEnvironmentType(metadata, environmentFilter)) {
        return false;
      }
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      if (!filterByCategory(metadata, categoryFilter)) {
        return false;
      }
    }

    // Filter by search query
    if (searchQuery) {
      if (!filterBySearchQuery(metadata, searchQuery)) {
        return false;
      }
    }

    return true;
  });
}
